/**
 * liveSTT.js — Gemini Live API session for STREAMING speech recognition.
 *
 * The browser connects DIRECTLY to Gemini's Live WebSocket, authenticated with
 * a short-lived ephemeral token minted by our backend (POST /api/live-token).
 * We stream 16 kHz PCM chunks up while the child speaks and read the
 * `inputTranscription` fragments coming back — Gemini is used as streaming
 * EARS only. The thinking (fuzzyMatch / evaluate-rescue) and the speaking
 * (cache-first pre-baked audio) stay exactly as designed — this removes the
 * record-then-upload wait that made turn-based STT slow, and removes the
 * Chrome-only Web Speech dependency.
 *
 * One session is opened per game session and reused across turns (each
 * connect costs a token mint + WS handshake, so we don't reconnect per turn).
 * If the socket drops, the next turn reconnects with a fresh token; if
 * connect fails entirely, the caller falls back to turn-based /api/listen.
 *
 * Protocol: v1alpha BidiGenerateContent over WebSocket. With ephemeral tokens
 * the endpoint is BidiGenerateContentConstrained and the token rides in the
 * access_token query param (per Google's ephemeral-token docs, 2026).
 */

const LIVE_WS_BASE =
  'wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1alpha.GenerativeService.BidiGenerateContentConstrained';

// The Live model always generates SOME response after each detected turn; we
// only want its ears, so the system prompt pins the reply to a throwaway "ok"
// (TEXT modality models). Transcription itself comes from inputTranscription.
const STT_SYSTEM_PROMPT =
  'The speaker is a young child (ages 3-10), often with a strong accent, answering a picture ' +
  'question with a short English phrase. The vocabulary is SMALL and FIXED — prefer these words ' +
  'whenever the audio is ambiguous: animals (cow/cows, pig/pigs, chicken/chickens, horse/horses, ' +
  'duck/ducks, sheep, goose/geese, rabbit/rabbits, turkey/turkeys, goat/goats, quail, donkey/donkeys), ' +
  'colors (black, pink, brown, white), ' +
  'numbers one through ten, in frames like "these are geese", "there are two rabbits", ' +
  '"I see two ducks", "these cows are black". ' +
  'Example: accented "these are geese" often sounds like "these are these" — ' +
  'the vocabulary tells you it is geese. ' +
  'There are NO dogs in the vocabulary: audio that sounds like "dog/dogs" is almost always ' +
  '"duck/ducks" — transcribe it as ducks unless the audio is unmistakably dog. ' +
  'MOST OF THESE CHILDREN ARE SPANISH SPEAKERS: if they speak Spanish, hear it as proper Spanish ' +
  'words (vacas, pollos, caballos, tres, marrón), never as garbled English. ' +
  'But NEVER transliterate accented ENGLISH into another script: when the child is attempting the ' +
  'English vocabulary, transcribe English words in Latin letters ("these are bunnies", NOT "दीज आर बनी" ' +
  'or "うさぎ には"). Use a non-Latin script ONLY when the child is truly speaking that language with ' +
  'its real words. ' +
  'Count questions are often answered with a bare number word — when a short answer is ambiguous, ' +
  'prefer the number words one..ten; color questions likewise prefer the color words. ' +
  'Transcribe numbers as WORDS in the language spoken ("two", "dos", "два") — NEVER as digits, ' +
  'because the digit hides which language the child used. ' +
  'You are used ONLY for transcription. Always reply with exactly: ok';

export class LiveSTT {
  constructor({ apiBase, logEvent }) {
    this.apiBase = apiBase;
    this.log = logEvent || (() => {});
    this.ws = null;
    this.ready = false;
    this.model = '';
    this.turnText = '';
    this.onPartial = null; // (textSoFar) => void, set per turn by the app
    this._waiters = [];
    this._connecting = null;
  }

  // Connect (or reuse the open session). Resolves when setupComplete arrives.
  ensureConnected() {
    if (this.ready && this.ws && this.ws.readyState === WebSocket.OPEN) return Promise.resolve(this);
    if (this._connecting) return this._connecting;
    this._connecting = this._connect().finally(() => { this._connecting = null; });
    return this._connecting;
  }

  async _connect() {
    const t0 = performance.now();
    const r = await fetch(`${this.apiBase}/api/live-token`, { method: 'POST' });
    if (!r.ok) throw new Error(`live-token ${r.status}`);
    const { token, model } = await r.json();
    if (!token) throw new Error('live-token empty');
    this.model = model;

    await new Promise((resolve, reject) => {
      let settled = false;
      const fail = (err) => { if (!settled) { settled = true; reject(err); } };
      const ws = new WebSocket(`${LIVE_WS_BASE}?access_token=${encodeURIComponent(token)}`);
      const timeout = setTimeout(() => { try { ws.close(); } catch { /* ignore */ } fail(new Error('live connect timeout')); }, 7000);

      ws.onopen = () => {
        // Verified against the real API 2026-07-02: current Live models are all
        // native-audio (TEXT modality is rejected), and thinkingBudget:0 is
        // essential — without it the model burns tokens "thinking" about its
        // throwaway reply (same gotcha as the 2.5-flash text endpoints).
        ws.send(JSON.stringify({
          setup: {
            model: `models/${model}`,
            generationConfig: { responseModalities: ['AUDIO'], thinkingConfig: { thinkingBudget: 0 } },
            systemInstruction: { parts: [{ text: STT_SYSTEM_PROMPT }] },
            inputAudioTranscription: {},
          },
        }));
      };
      ws.onmessage = async (ev) => {
        const msg = await parseWsMessage(ev);
        if (!msg) return;
        if (msg.setupComplete) {
          clearTimeout(timeout);
          this.ws = ws;
          this.ready = true;
          // Re-bind close/error for steady-state (no more rejecting).
          ws.onclose = (e) => {
            this.ready = false; this.ws = null;
            this.log('live-close', { code: e.code, reason: (e.reason || '').slice(0, 140) });
            this._release();
          };
          ws.onerror = () => {};
          this.log('live-open', { model, ms: Math.round(performance.now() - t0) });
          if (!settled) { settled = true; resolve(); }
          return;
        }
        this._handleMessage(msg);
      };
      ws.onerror = () => { clearTimeout(timeout); fail(new Error('live ws error')); };
      ws.onclose = (e) => { clearTimeout(timeout); fail(new Error(`live ws closed ${e.code} ${(e.reason || '').slice(0, 140)}`)); };
    });
    return this;
  }

  _handleMessage(msg) {
    const sc = msg.serverContent;
    if (!sc) return;
    if (sc.inputTranscription && sc.inputTranscription.text) {
      this.turnText += sc.inputTranscription.text;
      // Live captions (2026-10-20): let the UI show words as they stream in,
      // instead of waiting for end-of-turn. Never let a UI error break STT.
      if (this.onPartial) { try { this.onPartial(this.turnText); } catch { /* ignore */ } }
    }
    // turnComplete = the server's VAD decided the child finished + the model
    // replied. Everything for this turn is in — release any endTurn() waiter.
    if (sc.turnComplete) this._release();
  }

  _release() {
    const ws = this._waiters.splice(0);
    ws.forEach((fn) => fn());
  }

  // Start a fresh turn: clear the transcript accumulator.
  beginTurn() { this.turnText = ''; }

  // Stream one 16 kHz PCM chunk (base64) up to Gemini.
  sendAudioB64(b64) {
    if (!this.ready || !this.ws) return;
    try {
      this.ws.send(JSON.stringify({ realtimeInput: { audio: { data: b64, mimeType: 'audio/pcm;rate=16000' } } }));
    } catch { /* socket died mid-turn; endTurn/fallback will handle it */ }
  }

  // Our local silence detector says the child stopped. Tell the server the
  // audio stream paused, then wait briefly for the final transcription
  // fragments (resolves early on turnComplete). Returns the turn transcript.
  async endTurn(maxWaitMs = 1500) {
    if (this.ready && this.ws) {
      try { this.ws.send(JSON.stringify({ realtimeInput: { audioStreamEnd: true } })); } catch { /* ignore */ }
      // If we already have text, give it a grace window for trailing fragments;
      // if we have nothing yet, wait longer — it may still be in flight.
      // Grace raised 500→1000ms (2026-07-16): kids speaking sentences had the
      // final words ("...pink pigs") arrive after 500ms and get dropped. This
      // resolves EARLY on turnComplete, so a child who truly finished isn't made
      // to wait — the longer grace only bites when fragments are still in flight.
      const wait = this.turnText.trim() ? Math.min(1000, maxWaitMs) : maxWaitMs;
      await new Promise((resolve) => {
        const t = setTimeout(resolve, wait);
        this._waiters.push(() => { clearTimeout(t); resolve(); });
      });
    }
    return this.turnText.trim();
  }

  close() {
    try { if (this.ws) this.ws.close(); } catch { /* ignore */ }
    this.ws = null;
    this.ready = false;
  }
}

async function parseWsMessage(ev) {
  try {
    const text = typeof ev.data === 'string' ? ev.data : await ev.data.text();
    return JSON.parse(text);
  } catch { return null; }
}
